import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Gift, Heart, Menu, Search, ShoppingBag, User, X, LogOut, Package, LayoutDashboard, Truck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { api, inr } from "../lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Gifts", to: "/shop" },
  { label: "Categories", to: "/#categories" },
  { label: "Occasions", to: "/#occasions" },
  { label: "Same Day Delivery", to: "/shop?delivery=same_day" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { cartCount, wishlistIds } = useShop();
  const nav = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
    setUserMenu(false);
    setShowSug(false);
  }, [location.pathname, location.search]);

  const onSearchChange = (v) => {
    setQ(v);
    clearTimeout(debounce.current);
    if (!v.trim()) return setSuggestions([]);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products/suggest`, { params: { q: v } });
        setSuggestions(data.suggestions);
        setShowSug(true);
      } catch {
        setSuggestions([]);
      }
    }, 250);
  };

  const submitSearch = (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setShowSug(false);
    nav(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  const goHash = (to) => {
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      if (location.pathname !== path) nav(to);
      else document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else nav(to);
  };

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-amber-900/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center gap-4 h-16 lg:h-20">
          <button className="lg:hidden p-2 -ml-2 text-stone-700" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu" data-testid="mobile-menu-button">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <span className="w-9 h-9 rounded-xl bg-[#B45309] text-[#FDFBF7] grid place-items-center shadow-[0_6px_16px_-6px_rgba(180,83,9,0.6)]">
              <Gift size={19} strokeWidth={2.2} />
            </span>
            <span className="font-serif text-lg sm:text-2xl font-bold tracking-tight text-stone-900 leading-tight">
              Customize Gift <span className="text-[#B45309]">Online</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 ml-8" data-testid="desktop-nav">
            {NAV.map((n) => (
              <button key={n.label} onClick={() => goHash(n.to)} className="text-sm font-medium text-stone-600 hover:text-[#B45309] transition-colors" data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </button>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:block relative flex-1 max-w-md ml-auto" data-testid="search-form">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                value={q}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => suggestions.length && setShowSug(true)}
                placeholder="Search gifts, occasions, categories…"
                className="w-full h-11 pl-10 pr-4 rounded-full bg-white border border-stone-200 text-sm outline-none focus:border-[#B45309]/50 focus:ring-2 focus:ring-[#B45309]/10 transition-shadow placeholder:text-stone-400"
                data-testid="search-input"
              />
            </div>
            <AnimatePresence>
              {showSug && suggestions.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} className="absolute top-12 left-0 right-0 bg-white rounded-2xl border border-stone-200 shadow-xl overflow-hidden z-50" data-testid="search-suggestions">
                  {suggestions.map((s) => (
                    <button key={s.id} type="button" onClick={() => nav(`/product/${s.id}`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#F9F3EA] text-left transition-colors" data-testid={`suggestion-${s.id}`}>
                      <img src={s.images?.[0]} alt={s.name} className="w-9 h-9 rounded-lg object-cover" />
                      <span className="text-sm text-stone-700 flex-1 truncate">{s.name}</span>
                      <span className="text-sm font-semibold text-[#B45309]">{inr(s.price)}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </form>

          <div className="flex items-center gap-1 sm:gap-2 ml-auto md:ml-0">
            <Link to="/wishlist" className="relative p-2.5 rounded-full hover:bg-[#F5EFE6] transition-colors" aria-label="Wishlist" data-testid="header-wishlist">
              <Heart size={20} className="text-stone-700" />
              {wishlistIds.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#B45309] text-white text-[10px] font-bold grid place-items-center" data-testid="wishlist-count">{wishlistIds.length}</span>}
            </Link>
            <Link to="/cart" className="relative p-2.5 rounded-full hover:bg-[#F5EFE6] transition-colors" aria-label="Cart" data-testid="header-cart">
              <ShoppingBag size={20} className="text-stone-700" />
              {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#B45309] text-white text-[10px] font-bold grid place-items-center" data-testid="cart-count">{cartCount}</span>}
            </Link>
            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenu(!userMenu)} className="flex items-center gap-2 p-2 rounded-full hover:bg-[#F5EFE6] transition-colors" data-testid="user-menu-button">
                  <span className="w-8 h-8 rounded-full bg-[#E0A96D]/30 text-[#B45309] grid place-items-center text-sm font-bold">
                    {user.name?.[0]?.toUpperCase() || <User size={16} />}
                  </span>
                </button>
                <AnimatePresence>
                  {userMenu && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} className="absolute right-0 top-12 w-52 bg-white rounded-2xl border border-stone-200 shadow-xl py-2 z-50" data-testid="user-menu">
                      <div className="px-4 py-2 border-b border-stone-100">
                        <p className="text-sm font-semibold text-stone-800 truncate">{user.name}</p>
                        <p className="text-xs text-stone-500 truncate">{user.email}</p>
                      </div>
                      <MenuLink icon={<User size={15} />} label="My Profile" onClick={() => nav("/profile")} testId="menu-profile" />
                      <MenuLink icon={<Package size={15} />} label="My Orders" onClick={() => nav("/orders")} testId="menu-orders" />
                      {user.role === "admin" && <MenuLink icon={<LayoutDashboard size={15} />} label="Admin Dashboard" onClick={() => nav("/admin")} testId="menu-admin" />}
                      <button onClick={async () => { await logout(); nav("/"); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-700 hover:bg-red-50 transition-colors" data-testid="menu-logout">
                        <LogOut size={15} /> Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to="/auth" className="hidden sm:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#1C1917] text-[#FDFBF7] text-sm font-semibold hover:bg-[#B45309] transition-colors" data-testid="header-login">
                <User size={15} /> Sign In
              </Link>
            )}
          </div>
        </div>

        <form onSubmit={submitSearch} className="md:hidden pb-3">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={q} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search gifts…" className="w-full h-10 pl-10 pr-4 rounded-full bg-white border border-stone-200 text-sm outline-none focus:border-[#B45309]/50" data-testid="search-input-mobile" />
          </div>
        </form>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden border-t border-stone-200/70 bg-[#FDFBF7]" data-testid="mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <button key={n.label} onClick={() => goHash(n.to)} className="text-left py-3 text-base font-medium text-stone-700 border-b border-stone-100 last:border-0" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </button>
              ))}
              <Link to="/shop?delivery=same_day" className="flex items-center gap-2 py-3 text-sm font-semibold text-[#B45309]">
                <Truck size={16} /> Same Day Delivery cities
              </Link>
              {!user && <Link to="/auth" className="mt-2 h-11 grid place-items-center rounded-full bg-[#1C1917] text-[#FDFBF7] text-sm font-semibold" data-testid="mobile-login">Sign In / Register</Link>}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function MenuLink({ icon, label, onClick, testId }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-700 hover:bg-[#F9F3EA] transition-colors" data-testid={testId}>
      {icon} {label}
    </button>
  );
}
